// src/pages/DashboardPage.js
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Fungsi untuk format angka
const formatNumber = (number) => new Intl.NumberFormat('id-ID').format(number);
const formatCurrency = (number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(number);

const options = {
  timeZone: 'Asia/Jakarta',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

function formatDateToISO(date) {
  const formattedDate = new Intl.DateTimeFormat('en-GB', options).format(date);
  const [day, month, year] = formattedDate.split('/');
  return `${year}-${month}-${day}`;
}

function DashboardPage() {
  const [chartData, setChartData] = useState({ monthly_data: [], total_data: {} });
  const [vendorPieData, setVendorPieData] = useState({ transaction_vendors: [], disbursement_vendors: [] });
  const [loading, setLoading] = useState(true);

  // State untuk filter tanggal
  //const today = new Date().toISOString().slice(0, 10);
  const getFirstDayOfYear = new Date(new Date().getFullYear(), 0, 1);
  const getToday = new Date();
  const today = formatDateToISO(getToday);
  const firstDayOfYear = formatDateToISO(getFirstDayOfYear);
  const [dateRange, setDateRange] = useState({ start: firstDayOfYear, end: today });

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        const apiUrl = `/api/charts?start_date=${dateRange.start}&end_date=${dateRange.end}`;
        const response = await fetch(apiUrl);
        const data = await response.json();
        setChartData(data);
      } catch (error) {
        console.error("Gagal mengambil data grafik:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchVendorPieData = async () => {
      try {
        const apiUrl = `/api/vendor-pie-charts?start_date=${dateRange.start}&end_date=${dateRange.end}`;
        const response = await fetch(apiUrl);
        const data = await response.json();
        setVendorPieData(data);
      } catch (error) {
        console.error("Gagal mengambil data pie chart vendor:", error);
      }
    };

    fetchChartData();
    fetchVendorPieData();
  }, [dateRange]);

  // Data untuk Pie Charts
  const totalAmount = (chartData.total_data?.total_amount?.B2B || 0) + (chartData.total_data?.total_amount?.B2C || 0);
  const pieDataAmount = [
    { name: 'B2B', value: chartData.total_data?.total_amount?.B2B || 0 },
    { name: 'B2C', value: chartData.total_data?.total_amount?.B2C || 0 },
  ].map(item => ({
    ...item,
    percentage: totalAmount > 0 ? ((item.value / totalAmount) * 100).toFixed(1) : 0
  }));

  const totalEntries = (chartData.total_data?.total_entries?.B2B || 0) + (chartData.total_data?.total_entries?.B2C || 0);
  const pieDataEntries = [
    { name: 'B2B', value: chartData.total_data?.total_entries?.B2B || 0 },
    { name: 'B2C', value: chartData.total_data?.total_entries?.B2C || 0 },
  ].map(item => ({
    ...item,
    percentage: totalEntries > 0 ? ((item.value / totalEntries) * 100).toFixed(1) : 0
  }));

  const COLORS = ['#0088FE', '#FF8042', '#00C49F', '#FFBB28', '#FF7C7C', '#8DD1E1', '#D084D0'];

  // Data untuk Vendor Pie Charts
  const filteredTransactionVendors = vendorPieData.transaction_vendors.filter(item => item.amount > 0);
  const totalTransactionAmount = filteredTransactionVendors.reduce((sum, item) => sum + item.amount, 0);
  const transactionVendorPieData = filteredTransactionVendors.map((item, index) => ({
    ...item,
    fill: COLORS[index % COLORS.length],
    percentage: totalTransactionAmount > 0 ? ((item.amount / totalTransactionAmount) * 100).toFixed(1) : 0
  }));

  const filteredDisbursementVendors = vendorPieData.disbursement_vendors.filter(item => item.amount > 0);
  const totalDisbursementAmount = filteredDisbursementVendors.reduce((sum, item) => sum + item.amount, 0);
  const disbursementVendorPieData = filteredDisbursementVendors.map((item, index) => ({
    ...item,
    fill: COLORS[index % COLORS.length],
    percentage: totalDisbursementAmount > 0 ? ((item.amount / totalDisbursementAmount) * 100).toFixed(1) : 0
  }));

  return (
    <div>
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="date-filter-container">
          <input type="date" value={dateRange.start} onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))} />
          <span>-</span>
          <input type="date" value={dateRange.end} onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))} />
        </div>
      </div>

      {loading ? (
        <div>Loading charts...</div>
      ) : (
        <div className="charts-container">
          {/* Bar Charts Section */}
          <div className="bar-charts-section">
            <div className="chart-container">
              <h3>Transaction Amount</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.monthly_data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={val => new Intl.NumberFormat('id-ID', { notation: 'compact' }).format(val)} />
                  <Tooltip formatter={(value, name, props) => {
                    const total = props.payload.b2b_total_amount + props.payload.b2c_total_amount;
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                    return [`${formatCurrency(value)} (${percentage}%)`, name];
                  }} />
                  <Legend />
                  <Bar dataKey="b2b_total_amount" name="B2B" fill="#0088FE" />
                  <Bar dataKey="b2c_total_amount" name="B2C" fill="#FF8042" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-container">
              <h3>Transaction Entries</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.monthly_data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={val => new Intl.NumberFormat('id-ID', { notation: 'compact' }).format(val)} />
                  <Tooltip formatter={(value, name, props) => {
                    const total = props.payload.b2b_total_entries + props.payload.b2c_total_entries;
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                    return [`${formatNumber(value)} (${percentage}%)`, name];
                  }} />
                  <Legend />
                  <Bar dataKey="b2b_total_entries" name="B2B" fill="#0088FE" />
                  <Bar dataKey="b2c_total_entries" name="B2C" fill="#FF8042" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Pie Charts Section */}
          <div className="pie-charts-section">
            <div className="chart-container pie-chart">
              <h3>Transaction Amount Split</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieDataAmount} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(entry) => `${entry.name}: ${entry.percentage}%`}>
                    {pieDataAmount.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => {
                    if (props.payload && props.payload.percentage !== undefined) {
                      return [`${formatCurrency(value)} (${props.payload.percentage}%)`, name];
                    }
                    return [formatCurrency(value), name];
                  }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-container pie-chart">
              <h3>Transaction Entries Split</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieDataEntries} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(entry) => `${entry.name}: ${entry.percentage}%`}>
                    {pieDataEntries.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => {
                    if (props.payload && props.payload.percentage !== undefined) {
                      return [`${formatNumber(value)} (${props.payload.percentage}%)`, name];
                    }
                    return [formatNumber(value), name];
                  }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Vendor Pie Charts Section */}
          <div className="vendor-pie-charts-section">
            <div className="chart-container pie-chart">
              <h3>Transaction by Vendor</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={transactionVendorPieData} dataKey="amount" nameKey="vendor" cx="50%" cy="50%" outerRadius={100}>
                    {transactionVendorPieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => {
                    if (props.payload && props.payload.percentage !== undefined) {
                      return [`${formatCurrency(value)} (${props.payload.percentage}%)`, name];
                    }
                    return [formatCurrency(value), name];
                  }} />
                  <Legend formatter={(value) => `${value} (${transactionVendorPieData.find(item => item.vendor === value)?.percentage || 0}%)`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-container pie-chart">
              <h3>Disbursement by Vendor</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={disbursementVendorPieData} dataKey="amount" nameKey="vendor" cx="50%" cy="50%" outerRadius={100}>
                    {disbursementVendorPieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => {
                    if (props.payload && props.payload.percentage !== undefined) {
                      return [`${formatCurrency(value)} (${props.payload.percentage}%)`, name];
                    }
                    return [formatCurrency(value), name];
                  }} />
                  <Legend formatter={(value) => `${value} (${disbursementVendorPieData.find(item => item.vendor === value)?.percentage || 0}%)`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;